import { useEffect, useState } from "react";
import api, { fmtMoney, fmtDate } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Target, FileText, ReceiptText, FileSignature, PackageCheck, Building2, CalendarDays, Wallet, HardHat } from "lucide-react";
import TechProjectTab from "@/components/technique/TechProjectTab";
import ProjectCover from "@/components/ProjectCover";

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="h-4 w-4 text-brand-500 mt-0.5 shrink-0" />
      <div><p className="text-[10px] uppercase font-mono text-slate-400">{label}</p><p className="text-sm font-medium text-slate-700">{value || "—"}</p></div>
    </div>
  );
}

function Section({ title, icon: Icon, rows, cols }) {
  return (
    <div className="rounded-lg border bg-white overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b"><Icon className="h-4 w-4 text-brand-600" /><span className="text-sm font-semibold">{title}</span><span className="ml-auto text-xs text-slate-400">{rows.length}</span></div>
      {rows.length === 0 ? <p className="px-4 py-3 text-sm text-slate-400">Aucun élément.</p> : (
        <div className="divide-y">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-2.5 text-sm">
              <span className="font-mono text-xs text-slate-500 w-24 shrink-0">{r.numero}</span>
              <span className="flex-1 truncate">{cols(r)}</span>
              {r.status && <StatusBadge value={r.status} />}
              {r.amount != null && <span className="font-mono text-xs font-bold text-slate-700">{fmtMoney(r.amount)}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ClientProjectDetailDialog({ projectId, onOpenChange, initialTab = "devis" }) {
  const [data, setData] = useState(null);
  const reload = () => api.get(`/client-projects/${projectId}`).then((r) => setData(r.data)).catch(() => setData({ error: true }));
  useEffect(() => {
    if (!projectId) { setData(null); return; }
    setData(null);
    reload();
  }, [projectId]); // eslint-disable-line

  const p = data?.project;
  return (
    <Dialog open={!!projectId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="client-project-detail">
        <DialogHeader><DialogTitle className="font-head">{p ? `${p.numero} · ${p.nom}` : "Fiche projet"}</DialogTitle></DialogHeader>
        {!data ? <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand-500" /></div> : data.error ? (
          <p className="py-8 text-center text-slate-400">Impossible de charger la fiche.</p>
        ) : (
          <div className="space-y-4">
            <ProjectCover project={p} onChanged={reload} />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 rounded-lg border bg-slate-50 p-4">
              <Info icon={Building2} label="Client" value={p.client_name} />
              <Info icon={Wallet} label="Budget" value={fmtMoney(p.budget)} />
              <Info icon={Target} label="Statut" value={<StatusBadge value={p.statut} />} />
              <Info icon={CalendarDays} label="Début" value={p.date_debut ? fmtDate(p.date_debut) : "—"} />
              <Info icon={CalendarDays} label="Fin" value={p.date_fin ? fmtDate(p.date_fin) : "—"} />
              <Info icon={Building2} label="Responsable" value={p.responsable} />
              <Info icon={HardHat} label="Coût main-d'œuvre" value={fmtMoney(data.labor?.total_cout || 0)} />
              <Info icon={Building2} label="Type de construction" value={p.type_construction || "—"} />
              <Info icon={Building2} label="Surface" value={p.surface || "—"} />
              <Info icon={Building2} label="Localisation" value={p.localisation || "—"} />
            </div>
            {p.description && <p className="text-sm text-slate-600 border-l-2 border-brand-200 pl-3">{p.description}</p>}

            <Tabs defaultValue={initialTab}>
              <TabsList className="flex-wrap h-auto">
                <TabsTrigger value="technique" data-testid="cp-tab-technique">Technique</TabsTrigger>
                <TabsTrigger value="devis" data-testid="cp-tab-devis">Devis ({data.quotes.length})</TabsTrigger>
                <TabsTrigger value="factures" data-testid="cp-tab-factures">Factures ({data.invoices.length})</TabsTrigger>
                <TabsTrigger value="contrats" data-testid="cp-tab-contrats">Contrats ({data.contracts.length})</TabsTrigger>
                <TabsTrigger value="livraisons" data-testid="cp-tab-livraisons">Livraisons ({data.deliveries.length})</TabsTrigger>
                <TabsTrigger value="opportunites" data-testid="cp-tab-opportunites">Opportunités ({data.opportunities.length})</TabsTrigger>
                <TabsTrigger value="mo" data-testid="cp-tab-mo">Main-d'œuvre ({data.labor?.workers?.length || 0})</TabsTrigger>
              </TabsList>
              <TabsContent value="technique"><TechProjectTab projectId={projectId} /></TabsContent>
              <TabsContent value="devis"><Section title="Devis" icon={FileText} rows={data.quotes.map((q) => ({ numero: q.numero, client: q.client_name, status: q.statut, amount: q.total_ttc }))} cols={(r) => r.client} /></TabsContent>
              <TabsContent value="factures"><Section title="Factures" icon={ReceiptText} rows={data.invoices.map((i) => ({ numero: i.numero, client: i.client_name, status: i.creance_etat, amount: i.total_ttc }))} cols={(r) => r.client} /></TabsContent>
              <TabsContent value="contrats"><Section title="Contrats" icon={FileSignature} rows={data.contracts.map((c) => ({ numero: c.numero, client: c.client_name, status: c.statut, amount: c.montant }))} cols={(r) => r.client} /></TabsContent>
              <TabsContent value="livraisons"><Section title="Livraisons" icon={PackageCheck} rows={data.deliveries.map((d) => ({ numero: d.numero, client: d.client_name, status: d.statut }))} cols={(r) => r.client} /></TabsContent>
              <TabsContent value="opportunites"><Section title="Opportunités" icon={Target} rows={data.opportunities.map((o) => ({ numero: o.numero, client: o.titre, status: o.stage, amount: o.montant_estime }))} cols={(r) => r.client} /></TabsContent>
              <TabsContent value="mo">
                <div className="rounded-lg border bg-white overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-50 border-b"><HardHat className="h-4 w-4 text-brand-600" /><span className="text-sm font-semibold">Main-d'œuvre pointée sur ce chantier</span><span className="ml-auto text-xs text-slate-500">{data.labor?.total_jours || 0} j · {fmtMoney(data.labor?.total_cout || 0)}</span></div>
                  {(data.labor?.workers || []).length === 0 ? <p className="px-4 py-3 text-sm text-slate-400">Aucun ouvrier pointé sur ce projet.</p> : (
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-[10px] uppercase text-slate-400"><tr><th className="text-left px-4 py-1.5">Ouvrier</th><th className="px-2 py-1.5 text-center">Jours</th><th className="px-2 py-1.5 text-right">Taux/j</th><th className="px-4 py-1.5 text-right">Coût</th></tr></thead>
                      <tbody className="divide-y">
                        {data.labor.workers.map((w, i) => (
                          <tr key={i} data-testid={`mo-row-${i}`}><td className="px-4 py-2">{w.nom}{w.specialite ? <span className="text-slate-400"> · {w.specialite}</span> : ""}</td><td className="px-2 py-2 text-center font-mono">{w.jours}</td><td className="px-2 py-2 text-right font-mono text-xs">{fmtMoney(w.taux)}</td><td className="px-4 py-2 text-right font-mono font-bold">{fmtMoney(w.cout)}</td></tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
